import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation  } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import BotonVolver from "../../components/BotonVolver";
import { hoyMadrid } from "../../utils/fecha";

// Devuelve 'black' o 'white' según contraste legible sobre un color de fondo hex
const getContrastColor = (hex) => {
  if (!hex) return '#000';
  const c = hex.replace('#','');
  const r = parseInt(c.substring(0,2),16);
  const g = parseInt(c.substring(2,4),16);
  const b = parseInt(c.substring(4,6),16);
  // luminancia relativa
  const lum = 0.2126*r + 0.7152*g + 0.0722*b;
  return lum > 150 ? '#000' : '#fff';
};

// Darken hex color by percent (0-100)
const darkenColor = (hex, percent) => {
  if (!hex) return hex;
  const c = hex.replace('#','');
  const num = parseInt(c,16);
  let r = (num >> 16) & 0xFF;
  let g = (num >> 8) & 0xFF;
  let b = num & 0xFF;
  r = Math.max(0, Math.min(255, Math.round(r * (100 - percent) / 100)));
  g = Math.max(0, Math.min(255, Math.round(g * (100 - percent) / 100)));
  b = Math.max(0, Math.min(255, Math.round(b * (100 - percent) / 100)));
  const rr = r.toString(16).padStart(2,'0');
  const gg = g.toString(16).padStart(2,'0');
  const bb = b.toString(16).padStart(2,'0');
  return `#${rr}${gg}${bb}`;
};

const PisoCrearCompras = () => {
  const [pisos, setPisos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtroRuta, setFiltroRuta] = useState("");
  const [supervisora, setSupervisora] = useState(null);
  const [drafts, setDrafts] = useState({}); // { id_piso: { precio_total, productos } }

  const { user } = useAuth();
  const usuario = user?.user || user;

  const location = useLocation();

  const navigate = useNavigate();

  useEffect(() => {
    if (!usuario) return;
    setSupervisora(usuario);
    fetchPisos(usuario);
    cargarDrafts();
  }, [usuario]);

  // Cargar drafts desde localStorage para todos los pisos disponibles
  const cargarDrafts = () => {
    try {
      const hoy = hoyMadrid(); // misma fecha que sella Crear compras / Carrito
      const nueva = {};
      // No conocemos aún los ids antes de fetch, pero podemos recorrer localStorage claves
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        const m = key.match(/^compraData_piso_(\d+)$/);
        if (m) {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const json = JSON.parse(raw);
            if (json.fecha !== hoy) {
              // Borrador de otro día (compra ya abandonada/otra semana): no mostrarlo
              // como "en progreso" ni dejarlo acumulándose sin más.
              localStorage.removeItem(key);
              continue;
            }
            const id_piso = parseInt(m[1], 10);
            const precio_total = typeof json.precio_total === 'number' ? json.precio_total : null;
            const productos = Array.isArray(json.productos) ? json.productos.length : 0;
            if (precio_total !== null && productos > 0) {
              nueva[id_piso] = { precio_total, productos };
            }
          } catch (e) {
            // ignorar parse errors
          }
        }
      }
      setDrafts(nueva);
    } catch (e) {
      console.error('Error leyendo drafts', e);
    }
  };

  // Recalcular drafts cuando se enfoca la ventana (por si se creó/modificó en otra pestaña)
  useEffect(() => {
    const handler = () => cargarDrafts();
    window.addEventListener('focus', handler);
    return () => window.removeEventListener('focus', handler);
  }, []);

  useEffect(() => {
      // Marcamos en sessionStorage que estamos en esta página
      sessionStorage.setItem("enPisoCrearCompras", "true");

      // Detectar recarga de página
      if (sessionStorage.getItem("enPisoCrearCompras") === "true") {
        // Limpiamos la marca
        sessionStorage.removeItem("enPisoCrearCompras");
        // Opcional: redirigir automáticamente al recargar
        // navigate("/supervisora");
      }
    }, [navigate]);


  const fetchPisos = async (usuario) => {
    try {
      const res = await axios.get(
        `/api/pisoCrearCompras/pisos_supervisora?user=${encodeURIComponent(usuario)}`
      );
      setPisos(res.data);
    } catch (err) {
      console.error("Error al cargar pisos:", err);
    }
  };

  const rutasUnicas = [...new Set(pisos.map((p) => p.ruta).filter((r) => r != null))].sort((a, b) => a - b);

  // Generar colores para rutas, rojo cambiado a verde
  const coloresRutas = [
    "#4ECDC4", // verde azulado
    "#4CAF50", 
    "#556270", 
    "#C7F464",     
    "#C44DFF", 
    "#FFB347"
  ];
  const obtenerColorRuta = (ruta) => {
    // Handle null/undefined
    if (ruta == null) return null;
    // Normalize to string for comparison because rutasUnicas may contain numbers
    const target = String(ruta);
    const idxFound = rutasUnicas.findIndex(r => String(r) === target);
    const index = idxFound === -1 ? 0 : idxFound % coloresRutas.length;
    return coloresRutas[index];
  };

  const pisosFiltrados = pisos.filter(
    (p) =>
      (p.abreviatura || "").toLowerCase().includes(busqueda.toLowerCase()) &&
      (filtroRuta === "" || (p.ruta != null && p.ruta.toString() === filtroRuta))
  );

  const irACrearCompra = (piso) => {
    // Limpiar flags de compra confirmada para este piso, para permitir crear una nueva
    sessionStorage.removeItem(`compraConfirmada_${piso.id}`);
    sessionStorage.removeItem(`crearCompras_token_${piso.id}`);
    navigate(`/supervisora/crearCompras/${piso.id}`, {
      state: {
        user_supervisora: supervisora,
        id_piso: piso.id,
        abreviatura: piso.abreviatura,
        origen: "PisoCrearCompras",        
      },
    });
  };

  return (
    <div style={{ maxWidth: "650px", margin: "0 auto", padding: "20px" }}>
      <BotonVolver fallback="/supervisora/compras" />
      <h2 style={{ textAlign: "center", marginBottom: "20px", fontSize: "28px" }}>Selecciona un piso</h2>
      {location.state?.aviso && (
        <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 10, padding: "12px 16px", marginBottom: 16, textAlign: "center", color: "#92400e", fontSize: 15, fontWeight: 600 }}>
          ⚠️ {location.state.aviso}
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "25px", justifyContent: "center" }}>
        <input
          type="text"
          placeholder="Buscar abreviatura..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{
            width: "160px",
            padding: "12px 12px",
            fontSize: "16px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        />
        <select
          value={filtroRuta}
          onChange={(e) => setFiltroRuta(e.target.value)}
          style={{
            width: "140px",
            padding: "12px 12px",
            fontSize: "16px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            cursor: "pointer",
            textAlign: "center",
            background: filtroRuta ? obtenerColorRuta(filtroRuta) : '#fff',
            color: filtroRuta ? (getContrastColor(obtenerColorRuta(filtroRuta))) : '#111',
            borderColor: filtroRuta ? darkenColor(obtenerColorRuta(filtroRuta), 20) : '#ccc',
            WebkitAppearance: 'none',
            appearance: 'none',
          }}
        >
          <option value="">Rutas (todas)</option>
          {rutasUnicas.map((ruta) => (
            <option
              key={ruta}
              value={ruta}
              style={{
                backgroundColor: obtenerColorRuta(ruta),
                color: "#fff",
                textAlign: "center",
              }}
            >
              Ruta {ruta}
            </option>
          ))}
        </select>
      </div>

      {/* Pisos */}
      {pisosFiltrados.map((piso) => {
        const tieneDraft = !!drafts[piso.id];
        return (
          <div
            key={piso.id}
            onClick={() => irACrearCompra(piso)}
            style={{
              marginBottom: '14px',
              borderRadius: '12px',
              background: 'linear-gradient(120deg, #f0f4ff, #e0ebff)',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'pointer',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform='scale(1.02)'; e.currentTarget.style.boxShadow='0 6px 14px rgba(0,0,0,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 3px 6px rgba(0,0,0,0.1)'; }}
          >
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 22px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:'22px', fontWeight:700, color:'#333' }}>{piso.abreviatura}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:2 }}>
                <div style={{
                  backgroundColor: obtenerColorRuta(piso.ruta),
                  color:'#fff',
                  padding:'8px 14px',
                  borderRadius:8,
                  fontWeight:600,
                  fontSize:16,
                  minWidth:10,
                  textAlign:'center'
                }}>Ruta {piso.ruta}</div>
                <div style={{ fontSize:24, color:'#007bff' }} title='Ir a crear compra'>→</div>
              </div>
            </div>
            {tieneDraft && (
              <div
                title={`Precio estimado ${drafts[piso.id].precio_total.toFixed(2)}€, ${drafts[piso.id].productos} productos (pendiente de finalizar)`}
                style={{
                  background:'#eaf1f8',
                  borderTop:'1px solid #d0d8e2',
                  padding:'6px 14px 8px',
                  display:'flex',
                  justifyContent:'space-between',
                  alignItems:'center',
                  fontFamily:"'Segoe UI',sans-serif",
                  transition:'background .15s',
                  gap:12
                }}
              >
                <div style={{display:'flex',flexDirection:'column',lineHeight:1.1}}>
                  <span style={{fontSize:12,fontWeight:600,letterSpacing:'.4px',color:'#526273'}}>{drafts[piso.id].productos} prod.</span>
                  <span style={{fontSize:10,fontWeight:600,letterSpacing:'.6px',color:'#b45309',textTransform:'uppercase'}}>pendiente finalizar</span>
                </div>
                <span style={{fontSize:18,fontWeight:700,color:'#153e75'}}>{drafts[piso.id].precio_total.toFixed(2)} €</span>
              </div>
            )}
          </div>
        );
      })}

      {pisosFiltrados.length === 0 && (
        <p style={{ textAlign: "center", marginTop: "30px", color: "#555", fontSize: "18px" }}>
          No se encontraron pisos.
        </p>
      )}
    </div>
  );
};

export default PisoCrearCompras;
